/** Espejo de backend/utils/subscriptionAccess.js para el front. */

const FULL_STATUSES = new Set(['active', 'trialing']);
const READ_ONLY_STATUSES = new Set(['past_due', 'grace_readonly']);
const BLOCKED_STATUSES = new Set(['pending_payment', 'suspended', 'canceled', 'purged']);
const MS_DAY = 24 * 60 * 60 * 1000;

function parseDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function isAdminUser(user) {
  return user?.isSuperAdmin === true;
}

function bypassSubscriptionMora(user) {
  if (user?.subscription?.smokeTest === true) return false;
  return isAdminUser(user);
}

function billingSubscription(user) {
  return user?.companyBillingSubscription || user?.effectiveSubscription || user?.subscription;
}

/** Tester beta sin suscripción Mercado Pago activa. */
export function isBetaProTesterNotConverted(userOrSub) {
  const sub = userOrSub?.subscription
    ? billingSubscription(userOrSub)
    : userOrSub;
  if (!sub || typeof sub !== 'object') return false;
  if (sub.mercadopagoPreapprovalId) return false;
  const provider = String(sub.paymentProvider || '').toLowerCase();
  if (provider === 'mercadopago') return false;
  const src = String(sub.source || '').toLowerCase();
  return Boolean(
    sub.betaProEndsAt
    || sub.betaProTester
    || provider === 'beta_comp'
    || src === 'beta_pro_free'
  );
}

export function getBetaProDaysRemaining(userOrSub) {
  const sub = userOrSub?.subscription
    ? billingSubscription(userOrSub)
    : userOrSub;
  const ends = parseDate(sub?.betaProEndsAt || sub?.endDate);
  if (!ends) return null;
  return Math.ceil((ends.getTime() - Date.now()) / MS_DAY);
}

function formatBetaEndLabel(userOrSub) {
  const sub = userOrSub?.subscription
    ? billingSubscription(userOrSub)
    : userOrSub;
  const ends = parseDate(sub?.betaProEndsAt || sub?.endDate);
  if (!ends) return null;
  return ends.toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' });
}

/** Resumen para Cuenta / Uso del plan. */
export function getBetaProAccountInfo(user) {
  if (!user || bypassSubscriptionMora(user)) return null;
  if (!isBetaProTesterNotConverted(user)) return null;

  const sub = billingSubscription(user);
  const status = String(sub?.status || '').toLowerCase();
  const days = getBetaProDaysRemaining(user);
  const endsLabel = formatBetaEndLabel(user);
  const expired = sub?.betaProExpiredAt || (days !== null && days < 0);

  if (['active', 'trialing'].includes(status) && days !== null && days >= 0) {
    return {
      phase: days <= 14 ? 'ending_soon' : 'active',
      daysRemaining: days,
      endsLabel,
      endsAt: sub?.betaProEndsAt || sub?.endDate || null,
      showActivateCta: days <= 30,
    };
  }

  if (expired && (status === 'grace_readonly' || status === 'past_due')) {
    return {
      phase: 'grace_after_beta',
      daysRemaining: days,
      endsLabel,
      graceEndsLabel: formatGraceEndsAt(user),
      showActivateCta: true,
    };
  }

  if (expired) {
    return {
      phase: 'ended',
      daysRemaining: days,
      endsLabel,
      showActivateCta: true,
    };
  }

  return {
    phase: 'unknown',
    daysRemaining: days,
    endsLabel,
    showActivateCta: true,
  };
}

/** Cartel beta en Inicio / Cuenta (no navbar). */
export function getBetaProBannerMessage(user) {
  if (!user || bypassSubscriptionMora(user)) return null;
  if (!isBetaProTesterNotConverted(user)) return null;

  const sub = billingSubscription(user);
  const status = String(sub?.status || '').toLowerCase();
  const days = getBetaProDaysRemaining(user);
  const expired = sub?.betaProExpiredAt || (days !== null && days < 0);

  if (['active', 'trialing'].includes(status) && days !== null && days >= 0) {
    const urgent = days <= 14;
    return {
      tone: urgent ? 'grace' : 'info',
      title: urgent ? 'Tu beta termina pronto' : 'Estás en la beta de Contsuite',
      body: urgent
        ? 'Activá la suscripción paga para seguir sin cortes cuando termine el beneficio gratuito.'
        : 'Tenés plan Profesional incluido; no necesitás Mercado Pago hasta la fecha de vencimiento.',
      ctaLabel: days <= 30 ? 'Activar suscripción' : 'Ver planes',
      ctaUrl: '/planes',
    };
  }

  if (expired && (status === 'grace_readonly' || status === 'past_due')) {
    const graceLabel = formatGraceEndsAt(user);
    return {
      tone: 'grace',
      title: 'Finalizó tu período beta',
      body: graceLabel
        ? `Tu beneficio gratuito terminó. Tenés solo lectura hasta el ${graceLabel}; después activá un plan pago para volver a operar.`
        : 'Tu beneficio beta terminó. Activá un plan pago en Contsuite para recuperar el acceso completo.',
      ctaLabel: 'Activar suscripción',
      ctaUrl: '/planes',
    };
  }

  if (expired && (status === 'suspended' || status === 'canceled')) {
    return {
      tone: 'blocked',
      title: 'Período beta finalizado',
      body: 'Tu cuenta necesita una suscripción paga para volver a usar Contsuite con normalidad.',
      ctaLabel: 'Ver planes',
      ctaUrl: '/planes',
    };
  }

  return null;
}

/** @returns {'full' | 'read_only' | 'blocked'} */
export function getSubscriptionAccessMode(user) {
  if (!user) return 'blocked';
  if (bypassSubscriptionMora(user)) return 'full';

  const billingSub = user.companyBillingSubscription || user.effectiveSubscription || user.subscription;
  const status = String(billingSub?.status || '').toLowerCase();
  if (!status) return 'full';
  if (FULL_STATUSES.has(status)) return 'full';

  if (READ_ONLY_STATUSES.has(status)) {
    const graceEnds = parseDate(billingSub?.graceEndsAt);
    if (graceEnds && graceEnds.getTime() <= Date.now()) {
      return 'blocked';
    }
    return 'read_only';
  }

  if (BLOCKED_STATUSES.has(status)) return 'blocked';
  return 'blocked';
}

export function isSubscriptionReadOnly(user) {
  return getSubscriptionAccessMode(user) === 'read_only';
}

export function formatGraceEndsAt(user) {
  const d = parseDate(billingSubscription(user)?.graceEndsAt);
  if (!d) return null;
  return d.toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' });
}

/** Texto del cartel global de mora (Navbar / Planes). */
export function getSubscriptionMoraBannerMessage(user, { includeBlocked = false } = {}) {
  if (!user) return null;
  if (bypassSubscriptionMora(user)) return null;

  const billingSub = billingSubscription(user);
  const status = String(billingSub?.status || '').toLowerCase();

  if (billingSub?.cancelAtPeriodEnd && (status === 'active' || status === 'trialing')) {
    const until = parseDate(billingSub.endDate) || parseDate(billingSub.trialEndsAt);
    const untilLabel = until
      ? until.toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' })
      : 'el fin del período pagado';
    return {
      tone: 'grace',
      body: `Cancelaste la renovación. Podés usar el plan hasta el ${untilLabel}. Después necesitás elegir un plan de nuevo.`,
    };
  }

  const mode = getSubscriptionAccessMode(user);

  if (mode === 'read_only') {
    const betaBanner = getBetaProBannerMessage(user);
    if (betaBanner) {
      return {
        tone: betaBanner.tone,
        body: betaBanner.body,
      };
    }
    const graceLabel = formatGraceEndsAt(user);
    if (graceLabel) {
      return {
        tone: 'grace',
        body: `Tenés acceso de solo lectura hasta el ${graceLabel}. Podés ver y exportar tu información.`,
      };
    }
    return {
      tone: 'grace',
      body: 'Tenés acceso de solo lectura: podés ver y exportar tu información mientras regularizás el pago.',
    };
  }

  if (!includeBlocked || mode !== 'blocked') return null;

  if (status === 'suspended') {
    return {
      tone: 'blocked',
      body: 'Tu suscripción está suspendida por falta de pago. Tus datos se conservan; regularizá el plan para volver a la app.',
    };
  }
  if (status === 'grace_readonly' || status === 'past_due') {
    return {
      tone: 'blocked',
      body: 'El período de gracia terminó. Regularizá el pago en Mercado Pago para recuperar el acceso completo.',
    };
  }
  if (status === 'canceled') {
    return {
      tone: 'blocked',
      body: 'Tu suscripción está cancelada. Elegí un plan para reactivar Contsuite.',
    };
  }

  return null;
}

export function shouldShowSubscriptionMoraBanner(user, options) {
  return Boolean(getSubscriptionMoraBannerMessage(user, options));
}

/** Etiqueta del estado de suscripción para UI (Planes, Cuenta). */
export function getSubscriptionStatusPresentation(user) {
  if (!user) {
    return { label: 'Sin sesión', tone: 'muted' };
  }
  if (user.isSuperAdmin || user.role === 'admin') {
    return { label: 'Administrador', tone: 'muted' };
  }

  const mode = getSubscriptionAccessMode(user);
  const status = String(billingSubscription(user)?.status || '').toLowerCase();

  if (mode === 'read_only') {
    return { label: 'Gracia (solo lectura)', tone: 'warning' };
  }
  if (mode === 'blocked') {
    if (status === 'suspended') return { label: 'Suspendida', tone: 'danger' };
    if (status === 'pending_payment') return { label: 'Pago pendiente', tone: 'warning' };
    if (status === 'grace_readonly' || status === 'past_due') {
      return { label: 'Pago pendiente', tone: 'danger' };
    }
    if (status === 'canceled' || status === 'cancelled') {
      return { label: 'Cancelada', tone: 'danger' };
    }
    return { label: 'Pago pendiente', tone: 'danger' };
  }

  if (status === 'trialing') {
    if (billingSubscription(user)?.cancelAtPeriodEnd) {
      return { label: 'Prueba (sin renovación)', tone: 'warning' };
    }
    return { label: 'Período de prueba', tone: 'success' };
  }
  if (status === 'active' || status === 'authorized') {
    const viaCompany = user.role === 'employee' && (user.companyBillingSubscription || user.effectiveSubscription);
    if (billingSubscription(user)?.cancelAtPeriodEnd) {
      return { label: 'Activa hasta fin de período', tone: 'warning' };
    }
    return {
      label: viaCompany ? 'Activa (empresa)' : 'Activa',
      tone: 'success',
    };
  }
  if (status === 'pending_payment') return { label: 'Pago pendiente', tone: 'warning' };
  if (!status) return { label: 'Sin información', tone: 'muted' };
  return { label: 'Activa', tone: 'success' };
}

/** Usuario con cuenta existente que debe pagar / renovar en MP (no es alta nueva). */
export function mustRegularizeSubscription(user) {
  if (!user) return false;
  if (user.isSuperAdmin || user.role === 'admin') return false;
  const mode = getSubscriptionAccessMode(user);
  if (mode === 'read_only' || mode === 'blocked') return true;
  const status = String(billingSubscription(user)?.status || '').toLowerCase();
  return status === 'pending_payment' || status === 'past_due';
}

/** Acceso real a la app (login + escritura), distinto del pago MP. */
export function getAdminAppAccessPresentation(user) {
  if (!user) return { label: '—', tone: 'muted' };
  if (user.isSuperAdmin || user.role === 'admin') {
    return { label: 'Administrador', tone: 'muted' };
  }

  const manual = String(user.status || '').toLowerCase();
  if (manual === 'suspended') {
    return { label: 'Deshabilitado (ABM)', tone: 'danger' };
  }

  const viewUser = user.effectiveSubscription
    ? {
      ...user,
      subscription: user.effectiveSubscription,
      companyBillingSubscription: user.effectiveSubscription,
    }
    : user;

  const mode = getSubscriptionAccessMode(viewUser);
  if (mode === 'full') {
    return { label: 'Puede operar', tone: 'success' };
  }
  if (mode === 'read_only') {
    return { label: 'Solo lectura', tone: 'warning' };
  }
  return { label: 'Sin acceso', tone: 'danger' };
}

export function getAdminAppAccessMode(user) {
  if (!user) return 'blocked';
  if (user.isSuperAdmin || user.role === 'admin') return 'full';
  if (String(user.status || '').toLowerCase() === 'suspended') return 'blocked';
  const viewUser = user.effectiveSubscription
    ? { ...user, subscription: user.effectiveSubscription, companyBillingSubscription: user.effectiveSubscription }
    : user;
  return getSubscriptionAccessMode(viewUser);
}
